import React, { createContext, useContext, useState, useCallback } from 'react'

interface AccessibilityContextType {
  fontSize: number
  highContrast: boolean
  increaseFontSize: () => void
  decreaseFontSize: () => void
  toggleHighContrast: () => void
}

const AccessibilityContext = createContext<AccessibilityContextType | undefined>(undefined)

const FONT_SIZES = [100, 112, 125]

export function AccessibilityProvider({ children }: { children: React.ReactNode }) {
  const [fontSize, setFontSize] = useState(0)
  const [highContrast, setHighContrast] = useState(false)

  const increaseFontSize = useCallback(() => {
    setFontSize((prev) => Math.min(prev + 1, 2))
  }, [])

  const decreaseFontSize = useCallback(() => {
    setFontSize((prev) => Math.max(prev - 1, 0))
  }, [])

  const toggleHighContrast = useCallback(() => {
    setHighContrast((prev) => !prev)
  }, [])

  React.useEffect(() => {
    document.documentElement.style.fontSize = `${FONT_SIZES[fontSize]}%`
  }, [fontSize])

  React.useEffect(() => {
    if (highContrast) {
      document.documentElement.classList.add('high-contrast')
    } else {
      document.documentElement.classList.remove('high-contrast')
    }
  }, [highContrast])

  return (
    <AccessibilityContext.Provider
      value={{
        fontSize,
        highContrast,
        increaseFontSize,
        decreaseFontSize,
        toggleHighContrast,
      }}
    >
      {children}
    </AccessibilityContext.Provider>
  )
}

export function useAccessibility() {
  const context = useContext(AccessibilityContext)
  if (!context) {
    throw new Error('useAccessibility must be used within AccessibilityProvider')
  }
  return context
}